import {useEffect} from 'react';
import {ButtonProps as MUIButtonProps, CircularProgress} from '@mui/material';
import {green, red} from '@mui/material/colors';
import {useTheme} from '@mui/material/styles';
import {LoadingButton} from '@mui/lab';

export enum SubmitButtonState {
  Normal = 'normal',
  Loading = 'loading',
  Success = 'success',
  Invalid = 'invalid',
  Error = 'error',
}
export interface SubmitButtonProps extends MUIButtonProps {
  state: SubmitButtonState;
  onStateChange: (newState: SubmitButtonState) => void;
  normalStateLabel: string;
  loadingStateLabel: string;
  errorStateLabel: string;
  successStateLabel?: string;
  invalidStateLabel?: string;
}

/**
 * Submit button component.
 */
export const SubmitButton = ({
  state,
  onStateChange,
  normalStateLabel,
  loadingStateLabel,
  successStateLabel,
  errorStateLabel,
  invalidStateLabel,
  type,
  color,
  variant,
  disabled,
  onClick,
  sx,
  ...rest
}: SubmitButtonProps) => {
  // On Changes
  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (
      state === SubmitButtonState.Success ||
      state === SubmitButtonState.Error ||
      state === SubmitButtonState.Invalid
    ) {
      // Reset State
      timerId = setTimeout(() => {
        onStateChange(SubmitButtonState.Normal);
      }, 3000);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [state, onStateChange]);

  // Get theme
  const theme = useTheme();

  // Button style
  const buttonSx = {
    ...(state === SubmitButtonState.Success && {
      bgcolor: green[500],
      '&:hover': {
        bgcolor: green[700],
      },
    }),
    ...(state === SubmitButtonState.Error && {
      bgcolor: red[500],
      '&:hover': {
        bgcolor: red[700],
      },
    }),
    ...(state === SubmitButtonState.Invalid && {
      bgcolor: theme.palette.warning.main,
      '&:hover': {
        bgcolor: theme.palette.warning.main,
        filter: 'brightness(0.9)',
      },
    }),
  };

  const isLoading = state === SubmitButtonState.Loading;

  return (
    <LoadingButton
      startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
      loading={isLoading}
      loadingPosition={isLoading ? 'start' : undefined}
      type={type as any}
      variant={state === SubmitButtonState.Normal && variant ? variant : 'contained'}
      color={color ? color : 'primary'}
      disabled={disabled || isLoading}
      onClick={onClick}
      sx={{...buttonSx, ...sx}}
      {...rest}
    >
      {state === SubmitButtonState.Normal ? normalStateLabel : undefined}
      {state === SubmitButtonState.Loading ? loadingStateLabel : undefined}
      {state === SubmitButtonState.Success ? successStateLabel : undefined}
      {state === SubmitButtonState.Error ? errorStateLabel : undefined}
      {state === SubmitButtonState.Invalid ? invalidStateLabel : undefined}
    </LoadingButton>
  );
};
